import { useEffect, useMemo, useState } from 'react';
import { translateApolloError } from '@bloomays-lib/utils.shared';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import { WithErrorBoundary, Modal } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import {
  BLOOMER_RELAUNCH_CRA,
  CLIENT_RELAUNCH_CRA,
  CREATE_ACTIVITY_CUSTOMER_INVOICE_RETRY,
  CREATE_ACTIVITY_SUPPLIER_INVOICE_RETRY,
  DECLARE_ZERO_ACTIVITY_FOR_MISSION,
  ENSURE_ACTIVITY_FOR_MISSION,
  GET_CRA_BY_MISSION,
  GET_CRA_BY_MONTH,
  JOBS_COMPLETED_SUBSCRIPTION,
  JOBS_ERRORED_SUBSCRIPTION,
  UPLOAD_ACTIVITY_FILE,
  VALIDATE_CRA,
} from '@bloomays-lib/adapter.api-bloomer';
import {
  CRAStatus,
  CRADashboardItemGQL,
  Job,
  InvoiceStatus,
  IActivity,
  BrowserUploadFile,
} from '@bloomays-lib/types.shared';
import { ApolloError, useLazyQuery, useMutation, useSubscription } from '@apollo/client';
import { format } from 'date-fns/fp/format';
import {
  convertDateToStringFormat,
  dateToUTC,
  getPrivateAdminDashboardMissionPath,
  isZeroCRAActivity,
  lastDateOfMonth,
} from '@bloomays-lib/utils.shared';
import { useParams, useSearchParams } from 'react-router-dom';
import useLocalStorage from 'use-local-storage';
import ActivityDashboardV2 from '../organisms/ActivityDashboardV2';
import ChronologicalHistory from '../organisms/ChronologicalHistory';
import ActionInvoice from '../molecules/ActionInvoice';
import ActionForceCRA from '../molecules/ActionForceCRA';
import ActionZeroCRA from '../molecules/ActionZeroCRA';
import ActionUploadInvoice from '../molecules/ActionUploadInvoice';
import {
  CraDashboardRow,
  canRelaunchBloomer,
  canRelaunchClient,
  craRowsToCsv,
  isValidated,
} from '../../helpers/craDashboard';
import { useEditCraDaysManual } from '../../hooks/useEditCraDaysManual';
import { Logger } from '../../services/serviceLogger';
import { FileActivityObject } from '../../types/components';
import { useBreadcrumbStore } from '../../stores';
import { buildMissionBreadcrumbLabel } from '../../helpers/breadcrumb';
import { styled } from '@mui/material/styles';
const logger = Logger('MonthlyActivityDashboard');

/** CRA statuses that may be declared as a "zero CRA" (no day worked). */
const canStatusSupportZeroCRA = (status: CRAStatus) =>
  status === 'Non créé' || status === 'Attente signature Bloomer' || status === 'Signature non créee';

const isZeroDays = (row: CRADashboardItemGQL): boolean =>
  isZeroCRAActivity({
    nbDaysWorked: row.nbDaysWorked ?? 0,
    nbDaysMarkupWorked: row.nbDaysMarkupWorked ?? 0,
    nbDaysMarkup2Worked: row.nbDaysMarkup2Worked ?? 0,
    astreinte: row.nbDaysAstreinteWorked ?? 0,
  });

const notifyError =
  (t: (key: string, options?: Record<string, unknown>) => string) => (error: ApolloError, message: string) => {
    errorLogger(error, { extraInfos: message });
    // On affiche un message FR dérivé du code d'erreur métier plutôt que le
    // message technique anglais brut (TEC-3053).
    notify('error', translateApolloError(error, t));
  };

const sendNotification = (type: 'info' | 'success', message: string) => {
  notify(type, message, null, { hideProgressBar: true, autoClose: 1000 });
};

type ModalState = { open: boolean; row?: CraDashboardRow };

const MonthlyActivityDashboard = () => {
  const { id: missionId } = useParams<{ id: string }>();
  // Deeplink `/activities?orderNumber=...` (TEC-3291) — seeds the CRA search box so a BDC
  // drawer link can land directly on the activities tied to that purchase order.
  const [searchParams] = useSearchParams();
  const orderNumberDeeplink = searchParams.get('orderNumber')?.trim() || undefined;
  const { t } = useTranslation(['missionPlanning', 'random', 'KPI', 'mission', 'notify']);
  const sendError = notifyError(t);
  const optionLocalStorage = {
    serializer: (date: Date | undefined) => (date || new Date()).toISOString(),
    parser: (dateStr: string) => (dateStr ? new Date(dateStr) : new Date()),
  };
  const [date, setDate] = useLocalStorage<Date>('MonthlyActivityDashboardDate', new Date(), optionLocalStorage);

  // Action modals (force CRA, zero CRA, client / Bloomer invoice retry, history).
  const [forceCRAModal, setForceCRAModal] = useState<ModalState>({ open: false });
  const [zeroCRAModal, setZeroCRAModal] = useState<ModalState>({ open: false });
  const [customerInvoiceModal, setCustomerInvoiceModal] = useState<ModalState>({ open: false });
  const [supplierInvoiceModal, setSupplierInvoiceModal] = useState<ModalState>({ open: false });
  const [uploadInvoiceModal, setUploadInvoiceModal] = useState<ModalState>({ open: false });
  const [uploadAdditionalModal, setUploadAdditionalModal] = useState<ModalState>({ open: false });
  const [historyModal, setHistoryModal] = useState<{
    open: boolean;
    activityId: string | null;
    missionId: string | null;
    month: Date | null;
  }>({ open: false, activityId: null, missionId: null, month: null });

  const { save: saveCraDaysEdit, loading: savingCraDaysEdit } = useEditCraDaysManual();

  const gqlGetCRA = missionId ? GET_CRA_BY_MISSION : GET_CRA_BY_MONTH;
  type gqlReturnType = { getCraByMonth?: CRADashboardItemGQL[]; getCraByMission?: CRADashboardItemGQL[] };
  const variablesGetCRA = useMemo(() => {
    // `date` est un 1er du mois à minuit *locale* (Europe/Paris) : `toISOString()`
    // le décalait au mois précédent en UTC, d'où un listing du mauvais mois côté
    // back (TEC-3064). `dateToUTC` renvoie ce même jour calendaire à midi UTC,
    // donc le bon mois quelle que soit la timezone.
    return missionId ? { missionId: missionId } : { month: dateToUTC(date).toISOString() };
  }, [missionId, date]);

  const [loadCRA, { data: dataCRA, loading: loadingCRA, error: fetchingErrorCRA, refetch }] =
    useLazyQuery<gqlReturnType>(gqlGetCRA, {
      context: { clientName: 'api.bloomers' },
      variables: variablesGetCRA,
    });

  const [rowsCRADashboardItemGQL, setRowsCRADashboardItemGQL] = useState<CRADashboardItemGQL[]>(
    dataCRA?.getCraByMission || dataCRA?.getCraByMonth || [],
  );

  const setBreadcrumbLabel = useBreadcrumbStore((state) => state.setLabel);
  const clearBreadcrumbLabel = useBreadcrumbStore((state) => state.clearLabel);
  const missionRow = dataCRA?.getCraByMission?.[0];

  // Quand on consulte les CRA d'une mission précise, affiche son nom dans le fil d'Ariane.
  useEffect(() => {
    const crumb = buildMissionBreadcrumbLabel({
      label: missionRow?.missionLabel,
      bloomerFullName: missionRow?.bloomerFullName,
      clientName: missionRow?.clientSocietyName,
    });
    if (!missionId || !crumb) return;
    const path = getPrivateAdminDashboardMissionPath(missionId);
    setBreadcrumbLabel(path, crumb);
    return () => clearBreadcrumbLabel(path);
  }, [
    missionId,
    missionRow?.missionLabel,
    missionRow?.bloomerFullName,
    missionRow?.clientSocietyName,
    setBreadcrumbLabel,
    clearBreadcrumbLabel,
  ]);

  useEffect(() => {
    const craDataLoaded = dataCRA?.getCraByMission || dataCRA?.getCraByMonth;
    if (craDataLoaded) {
      setRowsCRADashboardItemGQL(craDataLoaded);
      logger.debug('useEffect rows', craDataLoaded);
    }
  }, [dataCRA?.getCraByMission, dataCRA?.getCraByMonth]);

  const [
    createActivityCustomerInvoiceRetry,
    { loading: loadingCreateActivityCustomerInvoiceRetry, error: errorCreateActivityCustomerInvoiceRetry },
  ] = useMutation<{ createActivityCustomerInvoiceRetry: Job }, { activityRecordID: string; force: boolean }>(
    CREATE_ACTIVITY_CUSTOMER_INVOICE_RETRY,
  );

  const [
    createActivitySupplierInvoiceRetry,
    { loading: loadingCreateSupplierCustomerInvoiceRetry, error: errorCreateSupplierCustomerInvoiceRetry },
  ] = useMutation<
    { createActivitySupplierInvoiceRetry: Job },
    { activityRecordID: string; force: boolean; byPassComplianceCheck: boolean }
  >(CREATE_ACTIVITY_SUPPLIER_INVOICE_RETRY);

  const [declareZeroActivityForMission] = useMutation<
    { declareZeroActivityForMission: IActivity },
    { input: { missionId: string; month: string } }
  >(DECLARE_ZERO_ACTIVITY_FOR_MISSION, {
    context: { clientName: 'api.bloomers' },
    refetchQueries: [{ query: gqlGetCRA, variables: variablesGetCRA, context: { clientName: 'api.bloomers' } }],
    awaitRefetchQueries: true,
  });

  const [ensureActivityForMission] = useMutation<
    { ensureActivityForMission: IActivity },
    { input: { missionId: string; month: string } }
  >(ENSURE_ACTIVITY_FOR_MISSION, { context: { clientName: 'api.bloomers' } });

  const [validateCRA] = useMutation<{ validateCRA: IActivity }, { activityRecordID: string; validate: boolean }>(
    VALIDATE_CRA,
    {
      context: { clientName: 'api.bloomers' },
      refetchQueries: [{ query: gqlGetCRA, variables: variablesGetCRA, context: { clientName: 'api.bloomers' } }],
      awaitRefetchQueries: true,
    },
  );

  const [uploadActivityFile, { loading: loadingUploadActivity }] = useMutation<
    { uploadActivityFile: boolean },
    { input: { activityRecordId: string; file: FileActivityObject; activityFileType: 'Invoice' | 'CRA' } }
  >(UPLOAD_ACTIVITY_FILE, {
    // No refetch here: uploading only stores the file. The supplier invoice
    // generation that follows is tracked via the jobs subscription (like the
    // relance), so an immediate refetch would wrongly reset the status to "Non créée".
    context: { clientName: 'api.bloomers' },
  });

  const [sendClientRelaunch, { data: dataClientRelaunch, loading: loadingClientRelaunch, error: errorClientRelaunch }] =
    useMutation(CLIENT_RELAUNCH_CRA);
  const [
    sendBloomerRelaunch,
    { data: dataBloomerRelaunch, loading: loadingBloomerRelaunch, error: errorBloomerRelaunch },
  ] = useMutation(BLOOMER_RELAUNCH_CRA);

  const findActivity = (activityRecordID: string, prev: CRADashboardItemGQL[]) => {
    const index = prev.findIndex((item) => item.activityId === activityRecordID);
    if (index === -1) return { item: null, index: -1 };
    return { item: prev[index], index };
  };

  const updateRowsStateStatus = (
    prev: CRADashboardItemGQL[],
    activityRecordID: string,
    newStatus: InvoiceStatus,
    field: 'bloomerInvoiceStatus' | 'clientInvoiceStatus',
  ) => {
    const { index } = findActivity(activityRecordID, prev);
    if (index === -1) return prev;
    return prev.map((item, i) => (i === index ? { ...item, [field]: newStatus } : item));
  };

  const onDataSubscription = (_newStatus: InvoiceStatus, jobId?: string, jobQueueName?: string) => {
    if (!jobId) return;
    const isSupplier = jobQueueName === 'createActivitySupplierInvoice';
    const isCustomer = jobQueueName === 'createActivityCustomerInvoice';
    if (!isSupplier && !isCustomer) return;
    const activityId = jobId.split('-')[1];
    // On remplace la ligne ENTIÈRE avec les données fraîches du refetch. Patcher seulement
    // le statut laissait l'ancien `lastError` en place : l'icône d'erreur (FactCell) restait
    // donc affichée après une régénération réussie, jusqu'au refresh complet de la page.
    void refetch().then((data) => {
      const newRows = data.data.getCraByMission || data.data.getCraByMonth || [];
      const { item: newRow } = findActivity(activityId, newRows);
      if (newRow) {
        setRowsCRADashboardItemGQL((prev) => prev.map((item) => (item.activityId === activityId ? newRow : item)));
      }
    });
  };

  const { data: dataSubscriptionError } = useSubscription<{
    jobErrored: { jobId: string; jobQueueName: string; jobError: { message: string } };
  }>(JOBS_ERRORED_SUBSCRIPTION, {
    onData: (v) => onDataSubscription('En erreur', v.data.data?.jobErrored.jobId, v.data.data?.jobErrored.jobQueueName),
  });

  const { data: dataSubscriptionComplete } = useSubscription<{ jobCompleted: { jobId: string; jobQueueName: string } }>(
    JOBS_COMPLETED_SUBSCRIPTION,
    {
      onData: (v) =>
        onDataSubscription('En erreur', v.data.data?.jobCompleted.jobId, v.data.data?.jobCompleted.jobQueueName),
    },
  );

  useEffect(() => {
    const job = dataSubscriptionError?.jobErrored;
    if (!job) return;
    if (job.jobQueueName !== 'createActivitySupplierInvoice' && job.jobQueueName !== 'createActivityCustomerInvoice') {
      return;
    }
    notify('error', 'Traitement executé mais avec des erreurs', job.jobError.message, {
      hideProgressBar: true,
      autoClose: false,
      toastId: `dataSubscriptionComplete-${job.jobId}`,
    });
  }, [dataSubscriptionError]);

  useEffect(() => {
    const job = dataSubscriptionComplete?.jobCompleted;
    if (!job) return;
    if (job.jobQueueName !== 'createActivitySupplierInvoice' && job.jobQueueName !== 'createActivityCustomerInvoice') {
      return;
    }
    notify('success', 'Traitement executé avec succès', job.jobId, {
      hideProgressBar: true,
      autoClose: false,
      toastId: `dataSubscriptionComplete-${job.jobId}`,
    });
  }, [dataSubscriptionComplete]);

  useEffect(() => {
    void loadCRA({ variables: variablesGetCRA });
  }, [missionId, loadCRA, variablesGetCRA]);

  useEffect(() => {
    if (fetchingErrorCRA) sendError(fetchingErrorCRA, 'Error, unable to retrieve getCra  data!');
  }, [sendError, fetchingErrorCRA]);

  useEffect(() => {
    if (errorCreateActivityCustomerInvoiceRetry)
      sendError(errorCreateActivityCustomerInvoiceRetry, 'Error lors de la regeneration de facture client!');
  }, [sendError, errorCreateActivityCustomerInvoiceRetry]);

  useEffect(() => {
    if (errorCreateSupplierCustomerInvoiceRetry)
      sendError(errorCreateSupplierCustomerInvoiceRetry, 'Error lors de la regeneration de facture bloomer!');
  }, [sendError, errorCreateSupplierCustomerInvoiceRetry]);

  useEffect(() => {
    if (errorClientRelaunch) sendError(errorClientRelaunch, 'Error lors de la relance client!');
  }, [sendError, errorClientRelaunch]);

  useEffect(() => {
    if (errorBloomerRelaunch) sendError(errorBloomerRelaunch, 'Error lors de la relance Bloomer!');
  }, [sendError, errorBloomerRelaunch]);

  useEffect(() => {
    if (loadingClientRelaunch || loadingBloomerRelaunch) sendNotification('info', 'Notification en cours ! :)');
    if (loadingCreateActivityCustomerInvoiceRetry)
      sendNotification('info', 'Regénération de la facture client en cours ! :)');
    if (loadingCreateSupplierCustomerInvoiceRetry)
      sendNotification('info', 'Regénération de la facture bloomer en cours ! :)');
  }, [
    loadingClientRelaunch,
    loadingBloomerRelaunch,
    loadingCreateActivityCustomerInvoiceRetry,
    loadingCreateSupplierCustomerInvoiceRetry,
  ]);

  useEffect(() => {
    if (dataBloomerRelaunch?.relaunchBloomerCRA)
      sendNotification('success', 'Relance bloomer envoyée avec succès ! :)');
  }, [dataBloomerRelaunch]);

  useEffect(() => {
    if (dataClientRelaunch?.relaunchInviteClientCRA)
      sendNotification('success', 'Relance client envoyée avec succès ! :)');
  }, [dataClientRelaunch]);

  // ── Optimistic invoice retries (called from the modals) ───────────────────

  const createActivityCustomerInvoice = async (activityRecordID: string, force = false) => {
    let prevStatus: InvoiceStatus;
    setRowsCRADashboardItemGQL((prev) => {
      const { item } = findActivity(activityRecordID, prev);
      prevStatus = item?.clientInvoiceStatus || 'Non créée';
      return updateRowsStateStatus(prev, activityRecordID, 'En cours', 'clientInvoiceStatus');
    });
    await createActivityCustomerInvoiceRetry({ variables: { activityRecordID, force } }).catch(() => {
      setRowsCRADashboardItemGQL((prev) =>
        updateRowsStateStatus(prev, activityRecordID, prevStatus, 'clientInvoiceStatus'),
      );
    });
  };

  const createActivitySupplierInvoice = async (
    activityRecordID: string,
    force: boolean,
    byPassComplianceCheck: boolean,
  ) => {
    let prevStatus: InvoiceStatus;
    setRowsCRADashboardItemGQL((prev) => {
      const { item } = findActivity(activityRecordID, prev);
      prevStatus = item?.bloomerInvoiceStatus || 'Non créée';
      return updateRowsStateStatus(prev, activityRecordID, 'En cours', 'bloomerInvoiceStatus');
    });
    await createActivitySupplierInvoiceRetry({ variables: { activityRecordID, force, byPassComplianceCheck } }).catch(
      () => {
        setRowsCRADashboardItemGQL((prev) =>
          updateRowsStateStatus(prev, activityRecordID, prevStatus, 'bloomerInvoiceStatus'),
        );
      },
    );
  };

  /**
   * CRA the invoice must be attached to. A supplier invoice can reach Bloomays before the
   * Bloomer declared his CRA: in that case an empty CRA is created first so the file has
   * somewhere to land (TEC-3262). Creating it validates nothing and bills nothing.
   */
  const resolveActivityIdForUpload = async (row: CraDashboardRow): Promise<string> => {
    if (row.activityId) return row.activityId;
    const month = row.activityMonth ? new Date(row.activityMonth) : date;
    const created = await ensureActivityForMission({
      variables: { input: { missionId: row.missionId, month: convertDateToStringFormat(lastDateOfMonth(month)) } },
    });
    const activityId = created.data?.ensureActivityForMission?.recordId;
    if (!activityId) {
      throw new Error('Aucun CRA disponible pour rattacher la facture');
    }
    return activityId;
  };

  // Upload the Bloomer invoice file — optimistically flips the Bloomer invoice
  // status to "En cours" (like the relance), reverting on error.
  const uploadBloomerInvoice = async (row: CraDashboardRow, file: BrowserUploadFile) => {
    const files: FileActivityObject = {
      urlInvoicesS3: file,
    };
    sendNotification('info', 'Upload de la facture en cours ! :)');
    try {
      const activityRecordID = await resolveActivityIdForUpload(row);
      await uploadActivityFile({
        variables: { input: { activityRecordId: activityRecordID, file: files, activityFileType: 'Invoice' } },
      });
      // An invoice received ahead of the CRA validation is only stored: no supplier invoice
      // is generated and the CRA keeps its status (TEC-3262). A refetch is enough to surface
      // the stored file on the line.
      if (!isValidated(row)) {
        await refetch();
        sendNotification('success', 'Facture stockée sur le CRA ! :)');
        return;
      }
      // Once the file is stored, kick off the supplier invoice generation: the status
      // flips to "En cours" optimistically and the job result is awaited through the
      // jobs subscription — exactly like the Bloomer invoice relance.
      await createActivitySupplierInvoice(activityRecordID, false, false);
    } catch (error) {
      sendError(error as ApolloError, "Error lors de l'upload de la facture Bloomer!");
    }
  };

  // Additional Bloomer invoice — manual processing, no generation job: just store
  // the file and refetch so the line reflects it (preview + filter).
  const uploadAdditionalInvoice = async (row: CraDashboardRow, file: BrowserUploadFile) => {
    const files: FileActivityObject = { urlAdditionalInvoicesS3: file };
    sendNotification('info', 'Upload de la facture additionnelle en cours ! :)');
    try {
      const activityRecordID = await resolveActivityIdForUpload(row);
      await uploadActivityFile({
        variables: { input: { activityRecordId: activityRecordID, file: files, activityFileType: 'Invoice' } },
        refetchQueries: [{ query: gqlGetCRA, variables: variablesGetCRA, context: { clientName: 'api.bloomers' } }],
        awaitRefetchQueries: true,
      });
      sendNotification('success', 'Facture additionnelle uploadée ! :)');
    } catch (error) {
      sendError(error as ApolloError, "Error lors de l'upload de la facture additionnelle!");
    }
  };

  // A background job is pending while a relance / upload mutation is in flight,
  // or while any invoice line is still "En cours" (job awaited via subscription).
  const jobsInProgress =
    loadingCreateActivityCustomerInvoiceRetry ||
    loadingCreateSupplierCustomerInvoiceRetry ||
    loadingClientRelaunch ||
    loadingBloomerRelaunch ||
    loadingUploadActivity ||
    rowsCRADashboardItemGQL.some(
      (row) => row.clientInvoiceStatus === 'En cours' || row.bloomerInvoiceStatus === 'En cours',
    );

  // ── Callbacks wired to the dashboard ──────────────────────────────────────

  const handleForceCRA = (row: CraDashboardRow) => {
    if (canStatusSupportZeroCRA(row.status) && isZeroDays(row)) {
      setZeroCRAModal({ open: true, row });
    } else {
      setForceCRAModal({ open: true, row });
    }
  };

  const handleRelaunchClient = (row: CraDashboardRow) => {
    void sendClientRelaunch({ variables: { signingDocumentId: row.signingDocumentId } });
  };

  const handleRelaunchBloomer = (row: CraDashboardRow) => {
    const month = row.activityMonth ? new Date(row.activityMonth) : date;
    void sendBloomerRelaunch({
      variables: { missionId: row.missionId, month: convertDateToStringFormat(lastDateOfMonth(month)) },
    });
  };

  const handleBulkRelaunch = (rows: CraDashboardRow[]) => {
    rows.forEach((row) => {
      if (canRelaunchClient(row)) handleRelaunchClient(row);
      else if (canRelaunchBloomer(row)) handleRelaunchBloomer(row);
    });
  };

  const handleExportSelection = (rows: CraDashboardRow[]) => {
    if (!rows.length) return;
    // BOM UTF-8 () pour qu'Excel lise correctement les accents.
    const blob = new Blob([`\uFEFF${craRowsToCsv(rows)}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cra-export-${convertDateToStringFormat(date)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveCraDaysEdit = async (input: Parameters<typeof saveCraDaysEdit>[0]) => {
    const updatedRow = await saveCraDaysEdit(input);
    setRowsCRADashboardItemGQL((prev) =>
      prev.map((item) => (item.activityId === updatedRow.activityId ? updatedRow : item)),
    );
    sendNotification('success', t('updateSuccess', { ns: 'notify' }));
  };

  return (
    <Container>
      <ActivityDashboardV2
        missionMode={!!missionId}
        currentMonth={date}
        loading={loadingCRA || jobsInProgress}
        rows={rowsCRADashboardItemGQL}
        onMonthChange={missionId ? undefined : (month) => setDate(month)}
        onForceCRA={handleForceCRA}
        onRelaunchClient={handleRelaunchClient}
        onRelaunchBloomer={handleRelaunchBloomer}
        onRetryClientInvoice={(row) => setCustomerInvoiceModal({ open: true, row })}
        onRetryBloomerInvoice={(row) => setSupplierInvoiceModal({ open: true, row })}
        onUploadBloomerInvoice={(row) => setUploadInvoiceModal({ open: true, row })}
        onUploadAdditionalInvoice={(row) => setUploadAdditionalModal({ open: true, row })}
        onViewHistory={(row) =>
          setHistoryModal({
            open: true,
            activityId: row.activityId,
            missionId: row.missionId,
            month: row.activityMonth ? new Date(row.activityMonth) : date,
          })
        }
        onSaveCraDaysEdit={handleSaveCraDaysEdit}
        savingCraDaysEdit={savingCraDaysEdit}
        onBulkRelaunch={handleBulkRelaunch}
        onExportSelection={handleExportSelection}
        initialSearchValue={orderNumberDeeplink}
      />

      <Modal open={forceCRAModal.open} onClose={() => setForceCRAModal({ open: false })}>
        {forceCRAModal.row ? (
          <ActionForceCRA
            missionLabel={forceCRAModal.row.missionLabel}
            activityMonth={forceCRAModal.row.activityMonth ?? undefined}
            activityId={forceCRAModal.row.activityId as string}
            craStatus={forceCRAModal.row.status}
            clickHandler={async (force: boolean) => {
              await validateCRA({
                variables: { validate: force, activityRecordID: forceCRAModal.row?.activityId as string },
              }).catch((error) => sendError(error as ApolloError, 'Error lors de la validation du CRA!'));
              setForceCRAModal({ open: false });
            }}
          />
        ) : undefined}
      </Modal>

      <Modal open={zeroCRAModal.open} onClose={() => setZeroCRAModal({ open: false })}>
        {zeroCRAModal.row ? (
          <ActionZeroCRA
            missionId={zeroCRAModal.row.missionId}
            missionLabel={zeroCRAModal.row.missionLabel}
            activityMonth={zeroCRAModal.row.activityMonth ?? undefined}
            craStatus={zeroCRAModal.row.status}
            clickHandler={async () => {
              const row = zeroCRAModal.row as CRADashboardItemGQL;
              const month = row.activityMonth ? new Date(row.activityMonth) : date;
              await declareZeroActivityForMission({
                variables: { input: { missionId: row.missionId, month: format('yyyy-MM-dd', month) } },
              }).catch((error) => sendError(error as ApolloError, 'Error lors de la déclaration du CRA à zéro !'));
              setZeroCRAModal({ open: false });
            }}
          />
        ) : undefined}
      </Modal>

      <Modal open={customerInvoiceModal.open} onClose={() => setCustomerInvoiceModal({ open: false })}>
        {customerInvoiceModal.row ? (
          <ActionInvoice
            mode="customer"
            invoiceLabel={customerInvoiceModal.row.activityId as string}
            activityId={customerInvoiceModal.row.activityId as string}
            lastError={customerInvoiceModal.row.clientInvoiceLastError as string}
            invoiceStatus={customerInvoiceModal.row.clientInvoiceStatus}
            clickHandler={async (force: boolean) => {
              await createActivityCustomerInvoice(customerInvoiceModal.row?.activityId as string, force);
              setCustomerInvoiceModal({ open: false });
            }}
          />
        ) : undefined}
      </Modal>

      <Modal open={supplierInvoiceModal.open} onClose={() => setSupplierInvoiceModal({ open: false })}>
        {supplierInvoiceModal.row ? (
          <ActionInvoice
            mode="supplier"
            invoiceLabel={supplierInvoiceModal.row.activityId as string}
            activityId={supplierInvoiceModal.row.activityId as string}
            lastError={supplierInvoiceModal.row.bloomerInvoiceLastError as string}
            invoiceStatus={supplierInvoiceModal.row.bloomerInvoiceStatus}
            supplierInvoiceStatus={supplierInvoiceModal.row.supplierInvoiceStatus}
            externalSupplierInvoiceId={supplierInvoiceModal.row.externalSupplierInvoiceId}
            clickHandler={async (force: boolean, byPass: boolean) => {
              await createActivitySupplierInvoice(supplierInvoiceModal.row?.activityId as string, force, byPass);
              setSupplierInvoiceModal({ open: false });
            }}
          />
        ) : undefined}
      </Modal>

      <Modal open={uploadInvoiceModal.open} onClose={() => setUploadInvoiceModal({ open: false })}>
        {uploadInvoiceModal.row ? (
          <ActionUploadInvoice
            activityId={uploadInvoiceModal.row.activityId}
            missionLabel={uploadInvoiceModal.row.missionLabel}
            activityMonth={uploadInvoiceModal.row.activityMonth ?? undefined}
            onUpload={async (file: BrowserUploadFile) => {
              await uploadBloomerInvoice(uploadInvoiceModal.row as CraDashboardRow, file);
            }}
            onClose={() => setUploadInvoiceModal({ open: false })}
          />
        ) : undefined}
      </Modal>

      <Modal open={uploadAdditionalModal.open} onClose={() => setUploadAdditionalModal({ open: false })}>
        {uploadAdditionalModal.row ? (
          <ActionUploadInvoice
            activityId={uploadAdditionalModal.row.activityId}
            missionLabel={uploadAdditionalModal.row.missionLabel}
            activityMonth={uploadAdditionalModal.row.activityMonth ?? undefined}
            fileLabel="la facture additionnelle"
            onUpload={async (file: BrowserUploadFile) => {
              await uploadAdditionalInvoice(uploadAdditionalModal.row as CraDashboardRow, file);
            }}
            onClose={() => setUploadAdditionalModal({ open: false })}
          />
        ) : undefined}
      </Modal>

      <Modal
        open={historyModal.open}
        onClose={() => setHistoryModal({ open: false, activityId: null, missionId: null, month: null })}
        additionnalCSS={{ width: '980px', padding: '32px', border: 'none', borderRadius: '20px' }}
      >
        {historyModal.open ? (
          <ChronologicalHistory
            activityId={historyModal.activityId}
            missionId={historyModal.missionId as string}
            month={historyModal.month as Date}
          />
        ) : (
          <div />
        )}
      </Modal>
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  margin: 'auto',
  width: '90%',
}));
export default WithErrorBoundary(MonthlyActivityDashboard);
